// apps/client/src/world/fishing.ts (M1-T15) — the 'fishing' layer of the OutdoorScene: your personal fish shadows (dark ellipse decals
// that drift near home), everyone's bobbers, splash rings, and your personal shells on the beach. Created on the first `out` snapshot
// by net/handlers/fishing.ts.
import * as THREE from 'three';
import type { FishShadowView, ResidentId, ShellView, Vec2 } from '@whiskerton/shared';
import type { AssetService } from '../assets/service';
import { patchObject } from '../engine/shader/worldPatch';
import type { Game } from '../game/game';

export interface FishingLayer {
  setShadows(s: readonly FishShadowView[]): void; setShells(s: readonly ShellView[]): Promise<void>; removeShell(id: string): void;
  /** `tip` (additive, optional) writes the caster's rod tip in world space, or answers false: a fishing line runs from it to the bobber. */
  showBobber(r: ResidentId, at: Vec2, tip?: RodTip): void; dip(r: ResidentId): void; plunge(r: ResidentId): void; clearBobber(r: ResidentId): void; splash(at: Vec2): void;
  shadows(): readonly FishShadowView[]; update(dt: number): void;
}
export type RodTip = (out: THREE.Vector3) => boolean;
/** How far a caster leans the rod forward while the line is out (radians; Avatar.leanHeld). */
export const ROD_LEAN = 0.9;
const layers = new WeakMap<object, FishingLayer>();
export const fishingLayerOf = (game: Game): FishingLayer | null => layers.get(game) ?? null;
/** Internal (beyond the M1-T15 contract): net/handlers/fishing.ts registers the layer it creates; tests register fakes. */
export function registerFishingLayer(game: Game | object, l: FishingLayer): void { layers.set(game, l); }

const WATER_Y = 0.02;                                                      // fallback water plane height when no map is known
const shadowMat = new THREE.MeshBasicMaterial({ color: '#1F3A4A', transparent: true, opacity: 0.38, depthWrite: false });
const bobberTop = new THREE.MeshStandardMaterial({ color: '#E0503A', roughness: 0.8 });
const bobberBottom = new THREE.MeshStandardMaterial({ color: '#FFFBF1', roughness: 0.8 });
const shadowGeo = new THREE.CircleGeometry(0.5, 20).rotateX(-Math.PI / 2);
/** The bobber's radius (M1 playtest: at 0.07 it was a speck on the water); it floats with its red cap above the surface. */
export const BOBBER_R = 0.12;
const topGeo = new THREE.SphereGeometry(BOBBER_R, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2);
const bottomGeo = new THREE.SphereGeometry(BOBBER_R, 14, 8, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2);
const ringGeo = new THREE.RingGeometry(0.1, 0.16, 24).rotateX(-Math.PI / 2);
/** A soft ring of ripples that stays round the floating bobber, so it reads against the water's own ripples. */
const haloGeo = new THREE.RingGeometry(BOBBER_R * 1.25, BOBBER_R * 1.9, 28).rotateX(-Math.PI / 2);
const haloMat = new THREE.MeshBasicMaterial({ color: '#F2F5EC', transparent: true, opacity: 0.55, depthWrite: false });
// M1 playtest: a 1 px GL line vanished on a phone (and on high-DPR screens generally). The line is a thin dark tube instead.
const lineMat = new THREE.MeshBasicMaterial({ color: '#3B2F25' });
/** Points along the fishing line; it sags by LINE_SAG of its length at the middle (a parabola), the way a slack line hangs. */
const LINE_POINTS = 12, LINE_SAG = 0.12;
/** The line's tube: LINE_SIDES vertices round each point, LINE_R world units from it. */
const LINE_SIDES = 5, LINE_R = 0.011;
const tipV = new THREE.Vector3(), lineP = Array.from({ length: LINE_POINTS }, () => new THREE.Vector3());
const tanV = new THREE.Vector3(), nV = new THREE.Vector3(), bV = new THREE.Vector3(), UP = new THREE.Vector3(0, 1, 0), X = new THREE.Vector3(1, 0, 0);
function lineTube(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry(), idx: number[] = [];
  for (let i = 0; i + 1 < LINE_POINTS; i++) for (let k = 0; k < LINE_SIDES; k++) {
    const a = i * LINE_SIDES + k, b = i * LINE_SIDES + ((k + 1) % LINE_SIDES), c = a + LINE_SIDES, d = b + LINE_SIDES;
    idx.push(a, c, b, b, c, d);
  }
  geo.setIndex(idx);
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(LINE_POINTS * LINE_SIDES * 3), 3));
  return geo;
}
/** Rings of the tube round the centreline `pts`. */
function layTube(geo: THREE.BufferGeometry, pts: readonly THREE.Vector3[]): void {
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  pts.forEach((p, i) => {
    tanV.subVectors(pts[Math.min(pts.length - 1, i + 1)]!, pts[Math.max(0, i - 1)]!).normalize();
    nV.crossVectors(tanV, Math.abs(tanV.y) > 0.95 ? X : UP).normalize();
    bV.crossVectors(tanV, nV);
    for (let k = 0; k < LINE_SIDES; k++) {
      const a = (k / LINE_SIDES) * Math.PI * 2, cs = Math.cos(a) * LINE_R, sn = Math.sin(a) * LINE_R;
      pos.setXYZ(i * LINE_SIDES + k, p.x + nV.x * cs + bV.x * sn, p.y + nV.y * cs + bV.y * sn, p.z + nV.z * cs + bV.z * sn);
    }
  });
  pos.needsUpdate = true;
  geo.computeBoundingSphere();
}

/** Beyond the M1-T15 contract, deps take an optional `waterAt` (additive; existing callers are unaffected): it gives the water surface under a point (rivers and ponds sit on their tier); without it, a flat plane. */
export function createFishingLayer(group: THREE.Group, deps: { assets: Pick<AssetService, 'model'>; heightAt(p: Vec2): number; waterAt?(p: Vec2): number }): FishingLayer {
  const shadowGroup = new THREE.Group(); shadowGroup.name = 'shadows';
  const shellGroup = new THREE.Group(); shellGroup.name = 'shells';
  group.add(shadowGroup, shellGroup);
  const water = (p: Vec2) => deps.waterAt?.(p) ?? WATER_Y;
  let current: FishShadowView[] = [];
  let shellGen = 0;
  const bobbers = new Map<string, { obj: THREE.Group; t: number; dip: number; y: number; line: THREE.Mesh | null; tip: RodTip | null }>();
  /** Re-lays a bobber's line from the rod tip (world space, into the layer's frame) to the top of the bobber, sagging in between. */
  const layLine = (b: { obj: THREE.Group; y: number; line: THREE.Mesh | null; tip: RodTip | null }) => {
    if (!b.line || !b.tip) return;
    if (!b.tip(tipV)) { b.line.visible = false; return; }
    group.updateWorldMatrix(true, false);
    group.worldToLocal(tipV);
    // The tube's origin sits at the bobber: the world shader clips a mesh by its origin's distance from the camera (the horizon), so
    // vertices laid in layer coordinates round an origin at (0, 0) vanished everywhere far from the map's corner.
    const end = b.obj.position, sag = tipV.distanceTo(end) * LINE_SAG;
    b.line.position.set(end.x, b.y, end.z);
    tipV.sub(b.line.position);
    const ex = end.x - b.line.position.x, ey = end.y + BOBBER_R * 0.9 - b.line.position.y, ez = end.z - b.line.position.z;
    for (let i = 0; i < LINE_POINTS; i++) {
      const k = i / (LINE_POINTS - 1);
      lineP[i]!.set(tipV.x + (ex - tipV.x) * k, tipV.y + (ey - tipV.y) * k - sag * 4 * k * (1 - k), tipV.z + (ez - tipV.z) * k);
    }
    layTube(b.line.geometry, lineP);
    b.line.visible = true;
  };
  const splashes: { obj: THREE.Mesh; t: number }[] = [];
  let time = 0;
  const self: FishingLayer = {
    setShadows(list) {
      shadowGroup.clear(); current = [...list];
      for (const s of list) {
        const m = new THREE.Mesh(shadowGeo, shadowMat);
        const r = 0.18 + s.size * 0.07;                                    // shadow sizes 1–6
        m.scale.set(r * 1.6, 1, r);
        m.position.set(s.x, water(s) + 0.005, s.y);
        m.userData = { home: { x: s.x, y: s.y }, phase: s.x * 1.7 + s.y, id: s.id };
        m.renderOrder = 1;
        shadowGroup.add(m);
      }
      patchObject(shadowGroup);
    },
    async setShells(list) {
      const gen = ++shellGen;
      const built = await Promise.all(list.map(async (s) => {
        const obj = await deps.assets.model(s.catalogId); obj.name = `shell:${s.id}`;
        obj.position.set(s.x, deps.heightAt(s), s.y); obj.rotation.y = (s.x * 3 + s.y) % 6;
        return obj;
      }));
      if (gen !== shellGen) return;                                        // a newer list arrived while these loaded
      shellGroup.clear();
      for (const o of built) shellGroup.add(o);
      patchObject(shellGroup);
    },
    removeShell(id) { const o = shellGroup.getObjectByName(`shell:${id}`); if (o) shellGroup.remove(o); },
    showBobber(r, at, tip) {
      self.clearBobber(r);
      const obj = new THREE.Group(); obj.name = `bobber:${r}`;
      const halo = new THREE.Mesh(haloGeo, haloMat); halo.name = 'halo'; halo.position.y = -0.02; halo.renderOrder = 2;
      obj.add(new THREE.Mesh(topGeo, bobberTop), new THREE.Mesh(bottomGeo, bobberBottom), halo);
      const y = water(at) + 0.03;
      obj.position.set(at.x, y, at.y);
      patchObject(obj);
      group.add(obj);
      let line: THREE.Mesh | null = null;
      if (tip) {
        line = new THREE.Mesh(lineTube(), lineMat);
        line.name = `line:${r}`; line.visible = false; line.frustumCulled = false; line.userData.sides = LINE_SIDES;
        patchObject(line);
        group.add(line);
      }
      const b = { obj, t: 0, dip: 0, y, line, tip: tip ?? null };
      bobbers.set(r, b);
      layLine(b);
      self.splash(at);
    },
    dip(r) { const b = bobbers.get(r); if (b) b.dip = 0.25; },
    plunge(r) { const b = bobbers.get(r); if (b) b.dip = 1.2; },
    clearBobber(r) {
      const b = bobbers.get(r);
      if (!b) return;
      group.remove(b.obj);
      if (b.line) { group.remove(b.line); b.line.geometry.dispose(); }
      bobbers.delete(r);
    },
    splash(at) {
      const ring = new THREE.Mesh(ringGeo, new THREE.MeshBasicMaterial({ color: '#F2F5EC', transparent: true, opacity: 0.85, depthWrite: false }));
      ring.position.set(at.x, water(at) + 0.01, at.y);
      patchObject(ring);
      group.add(ring); splashes.push({ obj: ring, t: 0 });
    },
    shadows: () => current,
    update(dt) {
      time += dt;
      for (const m of shadowGroup.children) {                              // slow wander within 0.4 tiles of home
        const { home, phase } = m.userData as { home: Vec2; phase: number };
        m.position.x = home.x + Math.sin(time * 0.35 + phase) * 0.4; m.position.z = home.y + Math.cos(time * 0.27 + phase) * 0.3;
        m.rotation.y = Math.atan2(Math.cos(time * 0.35 + phase), -Math.sin(time * 0.27 + phase));
      }
      for (const b of bobbers.values()) {
        b.t += dt; b.dip = Math.max(0, b.dip - dt * 2.2);
        b.obj.position.y = b.y + Math.sin(b.t * 3) * 0.012 - Math.min(b.dip, 0.5) * 0.12;
        const halo = b.obj.getObjectByName('halo');
        if (halo) halo.position.y = b.y - 0.02 - b.obj.position.y;          // the ripples stay just on the water while the bobber bobs
        layLine(b);
      }
      for (let i = splashes.length - 1; i >= 0; i--) {
        const s = splashes[i]!; s.t += dt;
        const mat = s.obj.material as THREE.MeshBasicMaterial;
        s.obj.scale.setScalar(1 + s.t * 5); mat.opacity = Math.max(0, 0.85 - s.t * 1.4);
        if (s.t > 0.6) { group.remove(s.obj); mat.dispose(); splashes.splice(i, 1); }
      }
    },
  };
  return self;
}
